/**
 * @file HostingOrder.jsx
 * @description A modern and responsive order form for collecting domain name,
 *              number of users, billing cycle for Shared Hosting. Displays plan features.
 *              Includes validation for a valid domain/hostname (excluding localhost).
 * @author
 *   Masimba Maregedze
 * @version 1.3.1 // Version bump
 */

import React, { useState, useEffect, useRef } from "react";
import { Card, Form, Button, InputGroup, Row, Col } from "react-bootstrap"; // Re-added InputGroup
import { runData } from "../functions/api/processor";
import { cartData } from "../functions/api/cartData";
import { useProcessing } from "../context/ProcessingModal";
import { useReport } from "../context/ReportModal";
import { isAuthenticated } from "../utils/auth";

/**
 * @function isValidDomain
 * @description Checks whether the provided domain is valid.
 *              It does NOT allow "localhost" as a valid hostname.
 * @param {string} domain - The domain or hostname to validate.
 * @returns {boolean} True if valid, false otherwise.
 */
const isValidDomain = (domain) => {
  if (domain.toLowerCase() === "localhost") return false;
  const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(domain);
};

/**
 * @function HostingOrderForm
 * @description Renders a modern order form for Shared Hosting.
 * @returns {JSX.Element} A production-ready hosting order form component.
 */
const HostingOrderForm = () => {
  // State for order form values. User sets numberOfUsers.
  const [order, setOrder] = useState({
    product: "hosting",
    domainName: "",
    // Start with baseEmails once fetched, but default to 1 initially
    numberOfUsers: 1,
    billingCycle: "monthly",
  });

  const { setProcessing } = useProcessing();
  const { setReport } = useReport();
  const authenticated = isAuthenticated();

  // State for pricing details fetched from API.
  const [basePrice, setBasePrice] = useState(1.50); // Price for the whole base package
  const [currency, setCurrency] = useState("USD");
  const [totalPrice, setTotalPrice] = useState(1.50);
  // NEW: Store the total storage for the base package
  const [basePackageStorage, setBasePackageStorage] = useState(1);
  // Store the calculated storage PER USER in the base package
  const [storagePerBaseUser, setStoragePerBaseUser] = useState(1);
  const [baseWebsites, setBaseWebsites] = useState(1);
  const [baseEmails, setBaseEmails] = useState(1);

  // State for input validation errors.
  const [inputError, setInputError] = useState(""); // Re-added for numberOfUsers input
  const [domainError, setDomainError] = useState("");

  // State to control form submission.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // useRef to track if component is mounted.
  const isMountedRef = useRef(true);

  // Cleanup effect to avoid state updates on unmounted component.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * @function handleChange
   * @description Handles changes for the order form inputs.
   *              Validates "Number of Users" and "Domain Name".
   * @param {object} e - The event object from the form input.
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "numberOfUsers") {
      if (!/^\d*$/.test(value)) {
        setInputError("Please enter digits only.");
        return; // Prevent non-digit input
      }

      const numValue = value === "" ? "" : parseInt(value, 10); // Allow empty string temporarily

      if (value === "") {
         setInputError(`Number of users must be at least ${baseEmails}.`);
         // Keep the state temporarily empty or reset to baseEmails
         setOrder((prevOrder) => ({ ...prevOrder, [name]: "" })); // Allow temporary empty state
         return;
      }


      if (isNaN(numValue)) {
         setInputError(`Number of users must be at least ${baseEmails}.`);
         return; // Prevent NaN state
      }

      // Enforce minimum based on baseEmails
      if (numValue < baseEmails) {
        setInputError(`Number of users cannot be less than the base ${baseEmails}.`);
        // Optionally, force the value back to baseEmails immediately
        // setOrder((prevOrder) => ({ ...prevOrder, [name]: baseEmails }));
        // Or just show the error and let the user correct it
        setOrder((prevOrder) => ({ ...prevOrder, [name]: numValue })); // Update state to show invalid input temporarily
        return;
      }

      // If valid
      setInputError("");
      setOrder((prevOrder) => ({
        ...prevOrder,
        [name]: numValue,
      }));
    } else if (name === "domainName") {
      // ... existing domain name validation ...
      setOrder((prevOrder) => ({ ...prevOrder, [name]: value }));
      const trimmedValue = value.trim();
      if (trimmedValue === "") {
        setDomainError("Domain name cannot be empty.");
      } else if (trimmedValue.toLowerCase() === "localhost") {
        setDomainError("Hosting 'localhost' is not allowed.");
      } else if (!isValidDomain(trimmedValue)) {
        setDomainError("Please enter a valid domain.");
      } else {
        setDomainError("");
      }
    } else { // Handles billingCycle
      setOrder((prevOrder) => ({
        ...prevOrder,
        [name]: value,
      }));
    }
  };

  /**
   * @function handleSubmit
   * @description Submits the hosting order form details.
   * @param {object} e - The event object from form submission.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setProcessing(true);

    const trimmedDomain = order.domainName.trim();

    // Validate domain name.
    if (trimmedDomain === "" || !isValidDomain(trimmedDomain)) {
      setReport({
        show: true,
        message: "Please enter a valid domain.",
        type: "error",
      });
      setProcessing(false);
      setIsSubmitting(false);
      return;
    }

    // Re-add validation for number of users input against baseEmails
    if (order.numberOfUsers < baseEmails) {
      setReport({
        show: true,
        message: `Number of users must be at least ${baseEmails}.`,
        type: "error",
      });
      setProcessing(false);
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare the order data for the backend.
      // Use the user-inputted numberOfUsers.
      const orderPayloadForBackend = {
        product: "hosting",
        domainName: order.domainName,
        numberOfUsers: order.numberOfUsers, // Use user input
        billingCycle: order.billingCycle,
        // Include fetched plan details in the offer object for context
        offer: JSON.stringify({
            price: basePrice, // Base price per user
            currency: currency,
            storage: storagePerBaseUser, // Base storage per user
            websites: baseWebsites, // Included websites in plan
            emails: baseEmails // Base emails included in plan
        }),
      };

      const response = await runData(
        orderPayloadForBackend,
        `/${authenticated ? "api" : "web"}/orders/save/`
      );

      if (response.status === 200) {
        // Prepare payload for cart data, using calculatedTotal based on user input
        const orderPayloadForCart = {
          ...orderPayloadForBackend,
          calculatedTotal: totalPrice, // Total price based on user input
          reference: response.data.reference
        };
        await cartData("save", response.data.reference, orderPayloadForCart);

        setReport({
          show: true,
          message:
            "Your order has been saved successfully! Redirecting to checkout...",
          type: "success",
        });
        setTimeout(() => {
          window.location.href = "/checkout/";
        }, 3000);
      } else {
        setReport({
          show: true,
          message: response.data.message || "Order submission failed.",
          type: "error",
        });
      }
    } catch (err) {
      setReport({
        show: true,
        message:
          err.message ||
          "Order submission encountered an error. Please try again later.",
        type: "error",
      });
    }
    setProcessing(false);
    setIsSubmitting(false);
  };

  /**
   * @function fetchPricing
   * @description Fetches pricing, storage, websites, and emails details for Shared Hosting.
   */
  const fetchPricing = async () => {
    let fetchedPrice = 1.50;
    let fetchedCurrency = "USD";
    let fetchedTotalStorage = 1; // Store the total storage from API
    let fetchedWebsites = 1;
    let fetchedEmails = 1;
    let calculatedStoragePerUser = 1; // Calculate storage per base user

    try {
      const responseHosting = await runData({}, "/web/hosting/price/");
      if (
        responseHosting.status === 200 &&
        responseHosting.data &&
        responseHosting.data.price &&
        responseHosting.data.currency
      ) {
         fetchedPrice = Number(responseHosting.data.price) || 1.50;
         fetchedCurrency = responseHosting.data.currency;
         // Assume responseHosting.data.storage is the TOTAL for the base package
         fetchedTotalStorage = (responseHosting.data.storage && Number(responseHosting.data.storage) > 0) ? Number(responseHosting.data.storage) : 1;
         fetchedWebsites = (responseHosting.data.websites && Number(responseHosting.data.websites) >= 1) ? Number(responseHosting.data.websites) : 1;
         fetchedEmails = (responseHosting.data.emails && Number(responseHosting.data.emails) >= 1) ? Number(responseHosting.data.emails) : 1;

         // Calculate storage per base user
         calculatedStoragePerUser = fetchedEmails > 0 ? fetchedTotalStorage / fetchedEmails : fetchedTotalStorage;

      } else {
         console.warn("Invalid hosting price/details data received from API, using defaults.");
         // Use defaults if API fails
         calculatedStoragePerUser = fetchedTotalStorage / fetchedEmails;
      }
    } catch (err) {
      console.error("Failed to fetch price/details, using defaults:", err);
       // Use defaults on error
       calculatedStoragePerUser = fetchedTotalStorage / fetchedEmails;
    } finally {
       if (isMountedRef.current) {
          setBasePrice(fetchedPrice);
          setCurrency(fetchedCurrency);
          setBasePackageStorage(fetchedTotalStorage); // Set total base storage
          setStoragePerBaseUser(calculatedStoragePerUser); // Set calculated per-user storage
          setBaseWebsites(fetchedWebsites);
          setBaseEmails(fetchedEmails);

          setOrder(prevOrder => ({ ...prevOrder, numberOfUsers: fetchedEmails }));

          const initialMultiplier = order.billingCycle === "yearly" ? 12 : 1;
          setTotalPrice(fetchedPrice * initialMultiplier);
       }
    }
  };

  // Fetch pricing details on component mount.
  useEffect(() => {
    fetchPricing();
  }, []); // Keep dependency array empty if fetchPricing doesn't depend on changing props/state

  // Calculate total price based on number of users, billing cycle, base price, and base emails.
  useEffect(() => {
    const numericBasePrice = Number(basePrice) || 0;
    const numericBaseEmails = Number(baseEmails) || 1;

    // Calculate the price per base unit (email + its storage share)
    const pricePerBaseUnit = numericBaseEmails > 0 ? numericBasePrice / numericBaseEmails : numericBasePrice;

    // Total price is simply the number of users requested multiplied by the price per unit.
    // This assumes each user (base or additional) costs the same amount.
    const totalBeforeMultiplier = order.numberOfUsers * pricePerBaseUnit;

    // Apply billing cycle multiplier
    const multiplier = order.billingCycle === "yearly" ? 12 : 1;
    setTotalPrice(totalBeforeMultiplier * multiplier);

  }, [order.numberOfUsers, order.billingCycle, basePrice, baseEmails]); // Removed baseStoragePerUser dependency as it's implicit in pricePerBaseUnit

  // Determine if the form is valid
  const isFormValid =
    order.domainName.trim() !== "" &&
    domainError === "" &&
    inputError === "" &&
    order.numberOfUsers !== "" && // Ensure it's not empty
    !isNaN(order.numberOfUsers) && // Ensure it's a number
    order.numberOfUsers >= baseEmails; // Ensure it meets the minimum

  // Calculate total storage based on USER INPUT number of users and the calculated storage PER BASE USER
  const calculatedTotalStorage = order.numberOfUsers * storagePerBaseUser;

  return (
    <Card className="p-4 my-4 shadow-sm">
      <h3 className="mb-3">
        Order Shared Hosting
        <span className="float-end">
          {currency} {totalPrice.toFixed(2)}
        </span>
      </h3>
      <Form onSubmit={handleSubmit}>
        {/* Row 1: Domain Name and Number of Users */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="domainName">
              {/* Display included websites in label */}
              <Form.Label>Domain Name (Websites Included: {baseWebsites} | Unlimited Sub-Domains)</Form.Label>
              <Form.Control
                type="text"
                name="domainName"
                placeholder="Enter domain name (e.g. example.com)"
                value={order.domainName}
                onChange={handleChange}
                required
              />
              {domainError && (
                <Form.Text className="text-danger">{domainError}</Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="numberOfUsers">
              {/* Updated Label to show TOTAL base storage */}
              <Form.Label>Number of Email Users (From: {baseEmails} Emails | {basePackageStorage} GB Storage)</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  name="numberOfUsers"
                  min={baseEmails} // Set dynamic min value
                  value={order.numberOfUsers}
                  onChange={handleChange}
                  required
                  isInvalid={!!inputError} // Add isInvalid prop based on error state
                />
              </InputGroup>
              {/* Use Form.Control.Feedback for Bootstrap's built-in error display */}
              <Form.Control.Feedback type="invalid">
                 {inputError}
              </Form.Control.Feedback>
              {/* Display TOTAL calculated storage based on input */}
              <Form.Text className="text-muted mt-1">
                Total Storage Allocation: {calculatedTotalStorage.toFixed(1)} GB ({storagePerBaseUser.toFixed(1)} GB/user)
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {/* Row 2: Billing Cycle and Submit Button */}
        <Row className="align-items-end"> {/* Use align-items-end to align button with bottom of select */}
          <Col md={6}>
            <Form.Group className="mb-3 mb-md-0" controlId="billingCycle"> {/* Adjust margin for smaller screens */}
              <Form.Label>Billing Cycle</Form.Label>
              <Form.Select
                name="billingCycle"
                value={order.billingCycle}
                onChange={handleChange}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
             <Button
                variant="primary"
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="w-100" // Keep w-100 or adjust as needed for alignment
             >
               Submit Hosting Order
             </Button>
          </Col>
        </Row>

        {/* Removed the separate display column for plan details */}
        {/* Removed original button position */}
      </Form>
    </Card>
  );
};

export default HostingOrderForm;
